import { generateClientId } from '@seaber/common/lib/clientId';
import type {
  FilterCondition,
  FilterGroup,
  FilterItem,
  FilterLogic,
  FilterOperator,
  FilterState,
} from './types';

export function createEmptyState(
  topLevelLogic: FilterLogic = 'AND',
): FilterState {
  return { topLevelLogic, items: [] };
}

export function createCondition(
  fieldId: string,
  operator: FilterOperator,
  value: string | number | string[] | null,
): FilterCondition {
  return { id: generateClientId(), fieldId, operator, value };
}

export function createGroup(
  logic: FilterLogic,
  conditions: FilterItem[] = [],
): FilterGroup {
  return { id: generateClientId(), logic, conditions };
}

export function addCondition(
  state: FilterState,
  condition: FilterCondition,
): FilterState {
  return {
    ...state,
    items: [...state.items, { type: 'condition', condition }],
  };
}

const findAndRemoveItem = (
  items: FilterItem[],
  itemId: string,
): { items: FilterItem[]; found: boolean } => {
  const result: FilterItem[] = [];
  let found = false;

  for (const item of items) {
    if (item.type === 'condition' && item.condition.id === itemId) {
      found = true;
      continue;
    }
    if (item.type === 'group' && item.group.id === itemId) {
      found = true;
      continue;
    }

    if (item.type === 'group') {
      const nested = findAndRemoveItem(item.group.conditions, itemId);
      if (nested.found) {
        found = true;
        // If group becomes empty after removal, remove the group too
        if (nested.items.length === 0) {
          continue;
        }
        // If group has only one item, flatten it
        if (nested.items.length === 1) {
          result.push(nested.items[0]);
          continue;
        }
        result.push({
          type: 'group',
          group: { ...item.group, conditions: nested.items },
        });
        continue;
      }
    }

    result.push(item);
  }

  return { items: result, found };
};

export function removeItem(state: FilterState, itemId: string): FilterState {
  const { items } = findAndRemoveItem(state.items, itemId);
  return { ...state, items };
}

const updateConditionInItems = (
  items: FilterItem[],
  conditionId: string,
  updates: Partial<Omit<FilterCondition, 'id'>>,
): FilterItem[] => {
  return items.map((item): FilterItem => {
    if (item.type === 'condition' && item.condition.id === conditionId) {
      return {
        type: 'condition',
        condition: { ...item.condition, ...updates },
      };
    }
    if (item.type === 'group') {
      return {
        type: 'group',
        group: {
          ...item.group,
          conditions: updateConditionInItems(
            item.group.conditions,
            conditionId,
            updates,
          ),
        },
      };
    }
    return item;
  });
};

export function updateCondition(
  state: FilterState,
  conditionId: string,
  updates: Partial<Omit<FilterCondition, 'id'>>,
): FilterState {
  return {
    ...state,
    items: updateConditionInItems(state.items, conditionId, updates),
  };
}

export function setTopLevelLogic(
  state: FilterState,
  logic: FilterLogic,
): FilterState {
  return { ...state, topLevelLogic: logic };
}

const addConditionToGroupById = (
  items: FilterItem[],
  groupId: string,
  condition: FilterCondition,
): { items: FilterItem[]; added: boolean } => {
  const result: FilterItem[] = [];
  let added = false;

  for (const item of items) {
    if (item.type === 'group') {
      if (item.group.id === groupId) {
        result.push({
          type: 'group',
          group: {
            ...item.group,
            conditions: [
              ...item.group.conditions,
              { type: 'condition', condition },
            ],
          },
        });
        added = true;
        continue;
      }
      const nested = addConditionToGroupById(
        item.group.conditions,
        groupId,
        condition,
      );
      if (nested.added) {
        result.push({
          type: 'group',
          group: { ...item.group, conditions: nested.items },
        });
        added = true;
        continue;
      }
    }
    result.push(item);
  }

  return { items: result, added };
};

export function addConditionToGroup(
  state: FilterState,
  groupId: string,
  condition: FilterCondition,
): FilterState {
  const { items } = addConditionToGroupById(state.items, groupId, condition);
  return { ...state, items };
}

export function findParentGroup(
  state: FilterState,
  itemId: string,
): FilterGroup | null {
  const findInItems = (
    items: FilterItem[],
    parentGroup: FilterGroup | null,
  ): { found: true; parent: FilterGroup | null } | { found: false } => {
    for (const item of items) {
      if (item.type === 'condition' && item.condition.id === itemId) {
        return { found: true, parent: parentGroup };
      }
      if (item.type === 'group') {
        if (item.group.id === itemId) {
          return { found: true, parent: parentGroup };
        }
        const result = findInItems(item.group.conditions, item.group);
        if (result.found) return result;
      }
    }
    return { found: false };
  };

  const result = findInItems(state.items, null);
  return result.found ? result.parent : null;
}

export function attachConditionToItem(
  state: FilterState,
  targetId: string,
  logic: FilterLogic,
  condition: FilterCondition,
): FilterState {
  const parentGroup = findParentGroup(state, targetId);

  // Target is in a group with the same logic -- add to that group
  if (parentGroup && parentGroup.logic === logic) {
    return addConditionToGroup(state, parentGroup.id, condition);
  }

  // Target is standalone or in a group with different logic -- wrap in new group
  const wrapAndAdd = (items: FilterItem[]): FilterItem[] => {
    return items.map((item): FilterItem => {
      const itemId =
        item.type === 'condition' ? item.condition.id : item.group.id;

      if (itemId === targetId) {
        return {
          type: 'group',
          group: createGroup(logic, [item, { type: 'condition', condition }]),
        };
      }

      if (item.type === 'group') {
        return {
          type: 'group',
          group: {
            ...item.group,
            conditions: wrapAndAdd(item.group.conditions),
          },
        };
      }

      return item;
    });
  };

  return { ...state, items: wrapAndAdd(state.items) };
}
